import React, { useMemo, useState } from "react";
import { Scanner, type IDetectedBarcode } from "@yudiel/react-qr-scanner";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";
import { verifyTableQr } from "../../slices/TableQrs/thunk";
import { clearResolvedQr } from "../../slices/TableQrs/reducer";

const EXPECTED_QR_ROUTE = "/web-menu";
const INVALID_QR_MESSAGE = "Mã QR không hợp lệ.";

const getExpectedWebMenuUrl = () =>
    (process.env.REACT_APP_WEB_MENU_URL || window.location.origin).replace(/\/+$/, "");

type ScanStatus = "idle" | "checking" | "valid" | "invalid" | "error";

type ParsedQr = {
    token: string;
    url: URL;
};

const parseShopQrUrl = (rawValue: string, expectedWebMenuUrl: string): ParsedQr => {
    let url: URL;

    try {
        url = new URL(rawValue.trim());
    } catch (_) {
        throw new Error(INVALID_QR_MESSAGE);
    }

    const expectedUrl = new URL(expectedWebMenuUrl);

    if (url.hostname.toLowerCase() !== expectedUrl.hostname.toLowerCase()) {
        throw new Error(INVALID_QR_MESSAGE);
    }

    const pathParts = url.pathname.split("/").filter(Boolean);

    if (pathParts.length !== 2 || `/${pathParts[0]}` !== EXPECTED_QR_ROUTE) {
        throw new Error(INVALID_QR_MESSAGE);
    }

    const token = pathParts[1];

    if (!token || !/^[A-Za-z0-9_-]+$/.test(token)) {
        throw new Error(INVALID_QR_MESSAGE);
    }

    return { token, url };
};

const getTableLabel = (qr: any) => qr?.table?.name ?? qr?.table?.number ?? qr?.table_number ?? qr?.table?.table_number ?? qr?.table_id;

const QrCheck = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { resolving, resolveError } = useSelector((state: any) => state.TableQrs);

    const [status, setStatus] = useState<ScanStatus>("idle");
    const [message, setMessage] = useState("Đưa camera vào mã QR tại khu vực bàn để kiểm tra.");
    const [lastRawValue, setLastRawValue] = useState("");
    const [lastToken, setLastToken] = useState("");
    const [scannerError, setScannerError] = useState("");
    const [verifiedTable, setVerifiedTable] = useState<any | null>(null);
    const [paused, setPaused] = useState(false);

    const expectedWebMenuUrl = useMemo(() => getExpectedWebMenuUrl(), []);

    const resetScan = () => {
        dispatch(clearResolvedQr());
        setStatus("idle");
        setMessage("Đưa camera vào mã QR tại khu vực bàn để kiểm tra.");
        setLastRawValue("");
        setLastToken("");
        setScannerError("");
        setVerifiedTable(null);
        setPaused(false);
    };

    const verifyQrWithBackend = async (url: string) => {
        setStatus("checking");
        setMessage("Đang kiểm tra mã QR...");

        try {
            const response = await dispatch(verifyTableQr({ url })).unwrap() as any;

            if (!response?.valid) {
                setStatus("invalid");
                setMessage(INVALID_QR_MESSAGE);
                return;
            }

            setVerifiedTable(response.table ?? null);
            setStatus("valid");
            setMessage("Mã QR hợp lệ.");
        } catch (_) {
            setStatus("invalid");
            setMessage(INVALID_QR_MESSAGE);
        }
    };

    const handleScan = (detectedCodes: IDetectedBarcode[]) => {
        const rawValue = detectedCodes[0]?.rawValue;

        if (!rawValue || paused || resolving || status === "checking") return;

        setPaused(true);
        setLastRawValue(rawValue);
        setScannerError("");
        dispatch(clearResolvedQr());
        setVerifiedTable(null);

        try {
            const parsed = parseShopQrUrl(rawValue, expectedWebMenuUrl);
            setLastToken(parsed.token);
            verifyQrWithBackend(parsed.url.toString());
        } catch (_) {
            setStatus("invalid");
            setMessage(INVALID_QR_MESSAGE);
        }
    };

    const handleScannerError = () => {
        setScannerError("Không thể mở camera");
        setStatus("error");
    };

    const statusClass = {
        idle: "alert-secondary",
        checking: "alert-info",
        valid: "alert-success",
        invalid: "alert-danger",
        error: "alert-warning",
    }[status];

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />

                    <div className="page-heading">
                        <h6 className="mb-0">Kiểm tra mã QR</h6>
                    </div>

                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="card my-3">
                        <div className="card-body">
                            <h6 className="mb-2">Kiểm tra mã QR gọi món</h6>
                            <p className="text-muted small mb-2">
                                Quét mã QR tại khu vực bàn để xác nhận mã này thuộc quán hiện tại.
                            </p>

                            <div className="rounded overflow-hidden border bg-dark">
                                <Scanner
                                    onScan={handleScan}
                                    onError={handleScannerError}
                                    paused={paused}
                                    formats={["qr_code"]}
                                    constraints={{ facingMode: "environment", aspectRatio: 1 }}
                                    components={{ finder: true, torch: true, onOff: true }}
                                    scanDelay={800}
                                    styles={{
                                        container: { width: "100%" },
                                        video: { width: "100%", minHeight: 320, objectFit: "cover" },
                                    }}
                                />
                            </div>

                            <div className={`alert ${statusClass} mt-3 mb-0`} role="alert">
                                <div className="fw-bold mb-1">
                                    {status === "valid" && "Mã QR hợp lệ"}
                                    {status === "invalid" && "Mã QR không hợp lệ"}
                                    {status === "checking" && "Đang kiểm tra mã QR..."}
                                    {status === "error" && "Không thể mở camera"}
                                    {status === "idle" && "Sẵn sàng quét"}
                                </div>
                                <div>{scannerError || message || resolveError}</div>
                            </div>

                            {process.env.NODE_ENV !== "production" && lastRawValue && (
                                <div className="mt-3 small">
                                    <div className="text-muted">Nội dung QR vừa quét</div>
                                    <div className="text-break fw-semibold">{lastRawValue}</div>
                                </div>
                            )}

                            {process.env.NODE_ENV !== "production" && lastToken && (
                                <div className="mt-2 small">
                                    <span className="text-muted">QR token: </span>
                                    <span className="fw-semibold text-break">{lastToken}</span>
                                </div>
                            )}

                            {verifiedTable && status === "valid" && (
                                <div className="card bg-light border-0 mt-3">
                                    <div className="card-body py-3">
                                        <div className="fw-bold mb-1">Thông tin bàn</div>
                                        <div>Bàn: {getTableLabel({ table: verifiedTable }) ?? "Không xác định"}</div>
                                    </div>
                                </div>
                            )}

                            <button type="button" className="btn btn-danger w-100 mt-3" onClick={resetScan}>
                                Quét lại
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default QrCheck;
